import styles from './AssistedModeFilterDialog.module.css';

import { Field, FieldArray, Formik, useField } from 'formik';
import React, { useCallback } from 'react';

import Dialog from 'components/ui/Dialog';
import useTranslation from 'components/hooks/useTranslation';
import UiBlocker from 'components/bng/ui/UiBlocker';
import BngField from 'components/bng/form/BngField';
import BngForm from 'components/bng/form/BngForm';
import { DefaultDialogActions } from 'components/ui/FormUtils';
import BngSelectSearch from 'components/bng/form/BngSelectSearch';
import BngIconButton from 'components/bng/ui/BngIconButton';
import BngButton from 'components/bng/ui/BngButton';
import { BngMaskedField, Mask } from 'components/bng/form/BngMaskedField';

function OperatorToggle({ name }) {
  const { t } = useTranslation();

  const [field, meta, helpers] = useField(name);

  const handleToggle = (operator) => {
    helpers.setValue(operator);
  };

  return (
    <div className={styles.operatorToggle}>
      <BngButton
        onClick={() => handleToggle('AND')}
        className={`${styles.operatorButton} ${field.value === 'AND' ? styles.activeOperator : ''}`}
      >
        {t('AND')}
      </BngButton>
      <BngButton
        onClick={() => handleToggle('OR')}
        className={`${styles.operatorButton} ${field.value === 'OR' ? styles.activeOperator : ''}`}
      >
        {t('OR')}
      </BngButton>
    </div>
  );
}

export default function AssistedModeFilterDialog({ closeModal, nodeId, nodes = [], onChange = _.noop }) {
  const { t } = useTranslation();

  const comparisonOptions = {
    numeric: [
      { value: '=', label: `= ${t('ag-grid.equals')}` },
      { value: '!=', label: `≠ ${t('ag-grid.notEqual')}` },
      { value: '>', label: `> ${t('ag-grid.greaterThan')}` },
      { value: '>=', label: `≥ ${t('ag-grid.greaterThanOrEqual')}` },
      { value: '<', label: `< ${t('ag-grid.lessThan')}` },
      { value: '<=', label: `≤ ${t('ag-grid.lessThanOrEqual')}` },
      { value: 'IS NULL', label: t('ag-grid.blank') },
      { value: 'IS NOT NULL', label: t('ag-grid.notBlank') },
    ],
    text: [
      { value: 'LIKE_CONTAINS', label: `${t('ag-grid.contains')}` },
      { value: 'NOT_LIKE_CONTAINS', label: `${t('ag-grid.notContains')}` },
      { value: 'LIKE_STARTS_WITH', label: `${t('ag-grid.startsWith')}` },
      { value: 'LIKE_ENDS_WITH', label: `${t('ag-grid.endsWith')}` },
      { value: '=', label: `= ${t('ag-grid.equals')}` },
      { value: '!=', label: `≠ ${t('ag-grid.notEqual')}` },
      { value: 'IS NULL', label: t('ag-grid.blank') },
      { value: 'IS NOT NULL', label: t('ag-grid.notBlank') },
    ],
    bool: [{ value: '=', label: `=` }],
  };

  const handleSubmit = useCallback(
    (dialogValues) => {
      const updatedNodes = nodes.map((node) => {
        if (node.id === nodeId) {
          node = _.cloneDeep(node);
          node.data.tableInfo.filters = dialogValues.filters.map((filter, index) => {
            const selectedField = fields.find((f) => f.name === filter.selectedField);
            const fieldType = selectedField?.type;

            const condition = fieldType === 'BOOL' ? '=' : filter.condition;

            return {
              fieldName: filter.selectedField,
              condition: condition,
              value: filter.value,
              ...(index > 0 ? { operator: filter.operator } : {}),
            };
          });
        }

        return node;
      });
      onChange(updatedNodes);
      closeModal();
    },
    [nodes, nodeId]
  );

  const currentNode = nodes.find((node) => node.id === nodeId);
  const fields = currentNode?.data?.tableInfo?.fields || [];
  const existingFilters = currentNode?.data?.tableInfo?.filters || [];
  const initialFilters = existingFilters.map((filter, index) => ({
    selectedField: filter.fieldName || '',
    condition: filter.condition || '',
    value: filter.value || '',
    operator: index === 0 ? null : filter.operator || 'AND',
  }));
  const tableName = currentNode.data?.tableInfo?.table;

  if (initialFilters.length === 0) {
    initialFilters.push({ selectedField: '', condition: '', value: '', operator: null });
  }

  return (
    <Dialog
      className={`${styles.dialogContainer}`}
      title={`${t('filter')} - ${tableName}`}
      onClose={closeModal}
      newDialogLayout
    >
      <UiBlocker>
        <Formik
          initialValues={{
            filters: initialFilters,
          }}
          onSubmit={handleSubmit}
        >
          {({ values, submitCount, errors, setFieldValue }) => (
            <BngForm>
              <Dialog.Body className={styles.filtersWrapper}>
                <FieldArray name="filters">
                  {({ push, remove }) => (
                    <div className={styles.filterGroups}>
                      <div className={styles.filterHeader}>
                        <div className={styles.headerTitle}>{t('configure.filters')}</div>
                        <BngIconButton
                          icon={'add_circle'}
                          onClick={() => push({ selectedField: '', condition: '', value: '', operator: 'AND' })}
                          className={styles.addButton}
                        />
                      </div>
                      {values.filters.map((filter, index) => {
                        const selectedFieldName = values.filters[index].selectedField;
                        const selectedField = fields.find((f) => f.name === selectedFieldName);
                        const fieldType = selectedField?.type;

                        return (
                          <div key={index} className={styles.filterGroup}>
                            {index > 0 && <OperatorToggle name={`filters.${index}.operator`} />}
                            <div className={styles.filterFieldsConfigurationWrapper}>
                              <Field
                                name={`filters.${index}.selectedField`}
                                className={`${styles.filterFieldsSelect}`}
                                component={BngField}
                                label={t('field')}
                                inputComponent={BngSelectSearch}
                                options={fields.map((f) => ({
                                  value: f.name,
                                  label: f.name,
                                }))}
                              />
                              {fieldType !== 'BOOL' && (
                                <Field
                                  name={`filters.${index}.condition`}
                                  className={styles.filterFieldsSelect}
                                  component={BngField}
                                  label={t('condition')}
                                  inputComponent={BngSelectSearch}
                                  options={
                                    fieldType === 'NUMERIC' || fieldType === 'DATE'
                                      ? comparisonOptions.numeric
                                      : fieldType === 'TEXT'
                                      ? comparisonOptions.text
                                      : []
                                  }
                                />
                              )}
                              {fieldType === 'BOOL' ? (
                                <Field
                                  name={`filters.${index}.value`}
                                  className={styles.filterFieldsInput}
                                  component={BngField}
                                  label={t('value')}
                                  inputComponent={BngSelectSearch}
                                  options={[
                                    { value: 'TRUE', label: t('assisted.mode.filter.true') },
                                    { value: 'FALSE', label: t('assisted.mode.filter.false') },
                                  ]}
                                />
                              ) : fieldType === 'DATE' ? (
                                <Field
                                  name={`filters.${index}.value`}
                                  className={styles.filterFieldsInput}
                                  component={BngField}
                                  label={t('value')}
                                  inputComponent={BngMaskedField}
                                  mask={Mask.DATE}
                                  asDate="br"
                                />
                              ) : (
                                <Field
                                  name={`filters.${index}.value`}
                                  className={styles.filterFieldsInput}
                                  component={BngField}
                                  label={t('value')}
                                  placeholder={t('write.value.here')}
                                />
                              )}
                              <BngIconButton
                                icon={'close'}
                                onClick={() => {
                                  if (values.filters.length > 1) {
                                    remove(index);
                                  } else {
                                    setFieldValue(`filters.${index}`, {
                                      selectedField: '',
                                      condition: '',
                                      value: '',
                                    });
                                  }
                                }}
                                className={styles.removeButton}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </FieldArray>
              </Dialog.Body>

              <Dialog.Footer>
                <DefaultDialogActions
                  closeModal={closeModal}
                  submitCount={submitCount}
                  errors={errors}
                  okLabel="apply"
                  cancelLabel="cancel"
                />
              </Dialog.Footer>
            </BngForm>
          )}
        </Formik>
      </UiBlocker>
    </Dialog>
  );
}
